import type {
    FetchCallback,
    RequestInterceptor,
    ResponseInterceptor,
    WithInterceptOptions,
    RequestContext
} from './types';

export class InterceptBuilder {
    private fetchCallback!: FetchCallback;
    private requestInterceptors: RequestInterceptor[] = [];
    private responseInterceptors: ResponseInterceptor[] = [];

    constructor(fetchCallback: FetchCallback) {
        if (typeof window !== 'undefined') {
            this.fetchCallback = fetchCallback.bind(window);
        } else {
            this.fetchCallback = fetchCallback;
        }
    }

    // Add request interceptor
    public intercept(interceptor: RequestInterceptor, options?: WithInterceptOptions): this {
        const { index } = this.parseInterceptOptions('request', options);
        this.requestInterceptors.splice(index, 0, interceptor);
        return this;
    }

    // Add response interceptor
    public interceptResponse(interceptor: ResponseInterceptor, options?: WithInterceptOptions): this {
        const { index } = this.parseInterceptOptions('response', options);
        this.responseInterceptors.splice(index, 0, interceptor);
        return this;
    }

    private parseInterceptOptions(type: 'request' | 'response', options?: WithInterceptOptions) {
        const interceptors = type === 'request' ? this.requestInterceptors : this.responseInterceptors;

        if (options) {
            const { index } = options;
            return {
                index: index ?? interceptors.length
            };
        }

        return {
            index: interceptors.length
        };
    }

    /**
     * Builds a fetch function with request and response interceptors
     * @returns fetch function with interceptors
     */
    public build(): FetchCallback {
        const handler: FetchCallback = (input, init, options) => {
            const _ctx: RequestContext = { input, init, options };

            // Recursive request interceptors
            const runRequestInterceptor = (i: number, ctx: RequestContext): Promise<RequestContext> => {
                if (i >= this.requestInterceptors.length) {
                    return Promise.resolve(ctx);
                }
                return this.requestInterceptors[i](ctx, (nextCtx) => runRequestInterceptor(i + 1, nextCtx));
            };

            // Recursive response interceptors
            const runResponseInterceptor = (i: number, res: Response, req: RequestContext): Promise<Response> => {
                if (i >= this.responseInterceptors.length) {
                    return Promise.resolve(res);
                }
                return this.responseInterceptors[i](req, res, (nextReq: RequestContext, nextRes: Response) =>
                    runResponseInterceptor(i + 1, nextRes, nextReq)
                );
            };

            // Run request chain first
            return runRequestInterceptor(0, _ctx)
                .then(async (ctx) => {
                    const response = await this.fetchCallback(ctx.input, ctx.init);
                    return { response, context: ctx };
                })
                .then(({ response, context }) => runResponseInterceptor(0, response, context));
        };

        return handler;
    }
}
